import React, { useEffect, useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { Link } from "react-router-dom";
import {
  FaDownload,
  FaIdCard,
  FaSearch,
  FaSpinner,
  FaEye,
} from "react-icons/fa";
import axiosInstance from "../config/axiosInstance";
import API from "../config/config";
import { toast } from "react-toastify";

const csvHeaders = [
  { label: "Aadhaar Number", key: "aadhaarNumber" },
  { label: "Driver Name", key: "driverName" },
  { label: "Phone", key: "phone" },
  { label: "Driver Status", key: "driverStatus" },
  { label: "Driver ID", key: "driverId" },
  { label: "Driver Object ID", key: "driverObjectId" },
];

const DuplicateAadhaarDrivers = () => {
  const [loading, setLoading] = useState(true);
  const [flat, setFlat] = useState([]);
  const [groups, setGroups] = useState([]);
  const [totalGroups, setTotalGroups] = useState(0);
  const [totalDrivers, setTotalDrivers] = useState(0);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get(API.ADMIN.DUPLICATE_AADHAAR_LIST);
        if (res.data?.success) {
          setFlat(res.data.flat || []);
          setGroups(res.data.groups || []);
          setTotalGroups(res.data.totalGroups || 0);
          setTotalDrivers(res.data.totalDrivers || 0);
        } else {
          toast.error(
            res.data?.message || "Failed to load duplicate Aadhaar data"
          );
        }
      } catch (err) {
        console.error(err);
        toast.error(
          err.response?.data?.message ||
            "Failed to load duplicate Aadhaar drivers"
        );
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredFlat = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return flat;
    return flat.filter(
      (row) =>
        (row.aadhaarNumber || "").toLowerCase().includes(q) ||
        (row.driverName || "").toLowerCase().includes(q) ||
        (row.phone || "").toLowerCase().includes(q) ||
        (row.driverStatus || "").toLowerCase().includes(q) ||
        (row.driverId || "").toLowerCase().includes(q) ||
        (row.driverObjectId || "").toLowerCase().includes(q)
    );
  }, [flat, search]);

  const exportFilename = `duplicate-aadhaar-drivers-${new Date().toISOString().slice(0, 10)}.csv`;

  return (
    <div className="container mx-auto px-6 py-8 bg-gray-50 min-h-screen">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center space-x-3">
          <FaIdCard className="text-3xl text-blue-600" />
          <div>
            <h2 className="text-3xl font-bold text-gray-800">
              Duplicate Aadhaar Drivers
            </h2>
            <p className="text-sm text-gray-600">
              Drivers who share the same valid Aadhaar number with another
              driver. Empty, null, or zero Aadhaar values are excluded.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/drivers"
            className="inline-flex items-center justify-center px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-100"
          >
            Back to Drivers
          </Link>
          {!loading && filteredFlat.length > 0 && (
            <CSVLink
              data={filteredFlat}
              headers={csvHeaders}
              filename={exportFilename}
              className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg shadow-md text-sm font-medium"
            >
              <FaDownload />
              Existing Aadhaar Number Export
            </CSVLink>
          )}
        </div>
      </div>

      {!loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <p className="text-sm text-gray-500">Duplicate Aadhaar numbers</p>
            <p className="text-2xl font-bold text-gray-800">{totalGroups}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <p className="text-sm text-gray-500">Drivers in duplicate list</p>
            <p className="text-2xl font-bold text-gray-800">{totalDrivers}</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md border border-gray-200 mb-4 p-4">
        <div className="relative max-w-md">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by Aadhaar, name, phone, status, driver ID, or object ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <FaSpinner className="animate-spin text-4xl text-blue-600" />
        </div>
      ) : filteredFlat.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center text-gray-500">
          {flat.length === 0
            ? "No drivers with duplicate Aadhaar numbers found."
            : "No results match your search."}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Aadhaar Number
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Driver Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Driver Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Driver ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Driver Object ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredFlat.map((row, idx) => (
                  <tr
                    key={`${row.driverObjectId}-${idx}`}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {row.aadhaarNumber}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.driverName || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.phone || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.driverStatus || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.driverId || "—"}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700 font-mono">
                      {row.driverObjectId || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {row.driverObjectId ? (
                        <Link
                          to={`/drivers/detail/${row.driverObjectId}`}
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                        >
                          <FaEye />
                          View
                        </Link>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && groups.length > 0 && (
        <p className="mt-4 text-xs text-gray-500">
          Showing {filteredFlat.length} duplicate driver row(s)
          {search.trim() ? ` (filtered from ${flat.length})` : ""}. Export
          includes only drivers with a matching duplicate Aadhaar.
        </p>
      )}
    </div>
  );
};

export default DuplicateAadhaarDrivers;
